/**
 * RoleGuard Component
 * ====================
 * V4.162 - Updated for granular permission system
 *
 * Route protection component using the 26-resource permission system.
 * Wraps routes to enforce permission-based access control.
 *
 * Usage:
 * <Route path="/admin/revenue" element={
 *   <RoleGuard requiredResource="revenue_analytics">
 *     <RevenueAnalytics />
 *   </RoleGuard>
 * } />
 *
 * Legacy usage (still supported):
 * <Route path="/admin/revenue" element={
 *   <RoleGuard allowedRoles={['super_admin', 'finance']}>
 *     <RevenueAnalytics />
 *   </RoleGuard>
 * } />
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, Paper, Button, Chip } from '@mui/material';
import {
  Block as BlockIcon,
  Home as HomeIcon,
  Shield as ShieldIcon,
  Security as SecurityIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import {
  Permission,
  hasAccess,
  hasFullAccess,
  getResourceById,
  SUPER_ADMIN_EMAILS,
  countFullAccess,
  countViewOnly,
} from '../../config/permissionResources';

// Legacy types for backward compatibility
type AdminRole = 'super_admin' | 'finance' | 'marketing';

interface RoleGuardProps {
  children: React.ReactNode;
  /** NEW: Resource ID required to access this route (e.g., 'revenue_analytics') */
  requiredResource?: string;
  /** NEW: Minimum access level required ('view_only' or 'full') */
  requiredLevel?: 'view_only' | 'full';
  /** NEW: Pass if the user has access to ANY of these resources (OR semantics) */
  requiredResources?: string[];
  /** @deprecated Use requiredResource instead - Legacy roles allowed to access this route */
  allowedRoles?: AdminRole[];
  /** @deprecated Use requiredResource instead - Legacy specific permissions */
  requiredPermissions?: string[];
  /** Where to redirect if unauthorized (default: /dashboard/users for all) */
  redirectTo?: string;
  /** If true, shows an access denied page instead of redirecting */
  showAccessDenied?: boolean;
}

/**
 * Check if user is a hardcoded super admin
 */
const isSuperAdminEmail = (email: string): boolean => {
  return SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
};

/**
 * Access Denied Page Component
 * V4.162 - Updated for granular permission system
 */
const AccessDeniedPage: React.FC<{
  userPermissions: Permission[];
  requiredResource?: string;
  requiredLevel?: 'view_only' | 'full';
}> = ({ userPermissions, requiredResource, requiredLevel = 'view_only' }) => {
  const fullCount = countFullAccess(userPermissions);
  const viewCount = countViewOnly(userPermissions);
  const resource = requiredResource ? getResourceById(requiredResource) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 6,
          maxWidth: 500,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'error.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <BlockIcon sx={{ fontSize: 40, color: 'error.main' }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          Access Restricted
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You don't have permission to view this page.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'grey.50',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Your current permissions:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {fullCount > 0 && (
              <Chip
                size="small"
                icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                label={`${fullCount} Full`}
                sx={{
                  bgcolor: '#10B98120',
                  color: '#10B981',
                  border: '1px solid #10B98140',
                  '& .MuiChip-icon': { color: '#10B981' },
                }}
              />
            )}
            {viewCount > 0 && (
              <Chip
                size="small"
                icon={<ViewIcon sx={{ fontSize: 14 }} />}
                label={`${viewCount} View`}
                sx={{
                  bgcolor: '#6366F120',
                  color: '#6366F1',
                  border: '1px solid #6366F140',
                  '& .MuiChip-icon': { color: '#6366F1' },
                }}
              />
            )}
            {fullCount === 0 && viewCount === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No permissions assigned
              </Typography>
            )}
          </Box>
        </Paper>

        {resource && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This page requires:{' '}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: resource.color,
              }}
            >
              {resource.label} ({requiredLevel === 'full' ? 'Full Access' : 'View Only'})
            </Box>
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={<HomeIcon />}
          href="/dashboard/users"
          sx={{ mt: 2 }}
        >
          Go to User Management
        </Button>
      </Paper>
    </Box>
  );
};

/**
 * RoleGuard Component
 * V4.162 - Updated for granular permission system
 *
 * Protects routes based on the 26-resource permission system.
 * Supports both new `requiredResource` prop and legacy `allowedRoles` for backward compatibility.
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredResource,
  requiredResources,
  requiredLevel = 'view_only',
  allowedRoles,
  requiredPermissions,
  redirectTo = '/dashboard/users',
  showAccessDenied = true,
}) => {
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Get user permissions
  const userPermissions: Permission[] = user.permissions || [];

  // Hardcoded super admins can access everything
  if (user.email && isSuperAdminEmail(user.email)) {
    return <>{children}</>;
  }

  // NEW: OR semantics — pass if the user can access ANY of the listed resources
  if (requiredResources && requiredResources.length > 0) {
    const ok = requiredResources.some((r) =>
      requiredLevel === 'full' ? hasFullAccess(userPermissions, r) : hasAccess(userPermissions, r)
    );
    if (ok) return <>{children}</>;
    if (showAccessDenied) {
      return (
        <AccessDeniedPage
          userPermissions={userPermissions}
          requiredResource={requiredResources[0]}
          requiredLevel={requiredLevel}
        />
      );
    }
    return <Navigate to={redirectTo} replace />;
  }

  // NEW: Check permission-based access using requiredResource
  if (requiredResource) {
    const hasResourceAccess = requiredLevel === 'full'
      ? hasFullAccess(userPermissions, requiredResource)
      : hasAccess(userPermissions, requiredResource);

    if (hasResourceAccess) {
      return <>{children}</>;
    }

    // Unauthorized - show access denied page or redirect
    if (showAccessDenied) {
      return (
        <AccessDeniedPage
          userPermissions={userPermissions}
          requiredResource={requiredResource}
          requiredLevel={requiredLevel}
        />
      );
    }

    return <Navigate to={redirectTo} replace />;
  }

  // LEGACY: Support old allowedRoles prop for backward compatibility
  // Map legacy roles to permission checks
  if (allowedRoles && allowedRoles.length > 0) {
    // For legacy support, if user has any permissions, allow access based on role mapping
    const totalPermissions = userPermissions.length;
    const fullAccessCount = countFullAccess(userPermissions);

    // Legacy role mapping:
    // super_admin -> user has 20+ full access permissions
    // finance -> user has access to financial resources
    // marketing -> user has access to non-financial resources
    const hasLegacyAccess = allowedRoles.some(role => {
      if (role === 'super_admin') {
        return fullAccessCount >= 20; // Near-full access
      }
      if (role === 'finance') {
        return hasAccess(userPermissions, 'overview') ||
               hasAccess(userPermissions, 'revenue_analytics') ||
               hasAccess(userPermissions, 'cost_management');
      }
      if (role === 'marketing') {
        return hasAccess(userPermissions, 'user_management') ||
               hasAccess(userPermissions, 'chapter_creation') ||
               hasAccess(userPermissions, 'family_collaboration');
      }
      return false;
    });

    if (hasLegacyAccess) {
      return <>{children}</>;
    }

    // Unauthorized - show access denied page or redirect
    if (showAccessDenied) {
      return (
        <AccessDeniedPage
          userPermissions={userPermissions}
          requiredResource={undefined}
          requiredLevel="view_only"
        />
      );
    }

    return <Navigate to={redirectTo} replace />;
  }

  // No restrictions specified - allow access
  return <>{children}</>;
};

/**
 * Higher-order component version for class components
 * V4.162 - Updated for granular permission system
 */
export function withRoleGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredResource: string,
  requiredLevel: 'view_only' | 'full' = 'view_only'
) {
  return function WithRoleGuardComponent(props: P) {
    return (
      <RoleGuard requiredResource={requiredResource} requiredLevel={requiredLevel}>
        <WrappedComponent {...props} />
      </RoleGuard>
    );
  };
}

/**
 * Hook to check current user's permission access
 * V4.162 - Updated for granular permission system
 */
export function usePermissionAccess() {
  const { user } = useSelector((state: RootState) => state.auth);
  const userPermissions: Permission[] = user?.permissions || [];
  const isSuperAdmin = user?.email ? isSuperAdminEmail(user.email) : false;

  return {
    permissions: userPermissions,
    isSuperAdmin,
    fullAccessCount: countFullAccess(userPermissions),
    viewOnlyCount: countViewOnly(userPermissions),
    totalPermissions: userPermissions.filter(p => p.access_level !== 'none').length,

    // Permission checks
    hasAccess: (resource: string) => isSuperAdmin || hasAccess(userPermissions, resource),
    hasFullAccess: (resource: string) => isSuperAdmin || hasFullAccess(userPermissions, resource),

    // Convenience methods for common checks
    canViewFinancials: isSuperAdmin || hasAccess(userPermissions, 'overview') || hasAccess(userPermissions, 'revenue_analytics'),
    canManageUsers: isSuperAdmin || hasFullAccess(userPermissions, 'user_management'),
    canManageAdminUsers: isSuperAdmin || hasFullAccess(userPermissions, 'admin_users'),
    canViewContent: isSuperAdmin || hasAccess(userPermissions, 'chapter_creation'),
    canViewMonitoring: isSuperAdmin || hasAccess(userPermissions, 'aws_cloudwatch'),
    canViewSystem: isSuperAdmin || hasAccess(userPermissions, 'audit_log'),
  };
}

/**
 * @deprecated Use usePermissionAccess instead
 * Legacy hook maintained for backward compatibility
 */
export function useRoleAccess() {
  const { permissions, isSuperAdmin, hasAccess: checkAccess, hasFullAccess: checkFullAccess, ...rest } = usePermissionAccess();

  return {
    ...rest,
    isSuperAdmin,
    // Legacy role-style properties
    role: isSuperAdmin ? 'super_admin' : 'user',
    isFinance: checkAccess('revenue_analytics'),
    isMarketing: checkAccess('user_management'),
    canAccessFinancials: checkAccess('overview') || checkAccess('revenue_analytics'),
    canAccessMarketing: checkAccess('user_management') || checkAccess('chapter_creation'),
    canManageUsers: checkFullAccess('user_management'),
    canDeleteUsers: isSuperAdmin,
    canManageRoles: isSuperAdmin || checkFullAccess('admin_users'),
    hasPermission: (resource: string) => checkAccess(resource),
  };
}

export default RoleGuard;
